import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { apiGet, apiPost, errOf } from '../../lib/api';

interface TierRow {
  id: string;
  name_zh_hk: string;
  name_zh_cn: string;
  name_en: string;
  amount: number;
  benefits: string;
  status: string;
}
interface SponsorRow {
  id: string;
  company: string;
  stage: string;
  pledge_amount: number;
  paid_amount: number;
  tier_name_zh_hk: string | null;
  benefits_total: number;
  benefits_done: number;
}
interface Summary {
  total: number;
  byStage: Record<string, number>;
  pledged: number;
  paid: number;
  outstanding: number;
}
interface TierDraft {
  name_zh_hk: string;
  name_zh_cn: string;
  name_en: string;
  amount: number;
  benefits: string[];
}

const STAGES = ['lead', 'contacted', 'pitched', 'pledged', 'confirmed', 'paid', 'declined'] as const;
const field = {
  width: '100%',
  borderRadius: 8,
  padding: '6px 10px',
  border: '1px solid var(--color-divider)',
  background: 'var(--color-bg)',
  color: 'var(--color-text)',
  fontSize: 'var(--font-size-small)',
};
const btn = { background: 'var(--color-primary)', color: 'var(--color-on-primary)', border: 'none', cursor: 'pointer' } as const;
const card = { background: 'var(--color-card)', boxShadow: 'var(--shadow-card)' } as const;
const money = (n: number): string => `HK$${Number(n || 0).toLocaleString()}`;

/** 管理台「贊助」—— 方案（＋**AI 草擬**）／pipeline 漏斗 ／ 贊助商（Phase 3；decisions/008） */
export default function AdminSponsorsPage() {
  const { t } = useTranslation();
  const [tiers, setTiers] = useState<TierRow[]>([]);
  const [sponsors, setSponsors] = useState<SponsorRow[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [drafts, setDrafts] = useState<TierDraft[]>([]);

  const [tier, setTier] = useState({ code: '', name_zh_hk: '', name_zh_cn: '', name_en: '', amount: '', benefits: '' });
  const [sp, setSp] = useState({ company: '', contact_name: '', contact_email: '', contact_phone: '', tier_id: '', pledge_amount: '' });

  const load = useCallback(async () => {
    const r = await apiGet<{ sponsors: SponsorRow[]; tiers: TierRow[]; summary: Summary }>('/api/admin/sponsors');
    if (r.ok && r.data) {
      setSponsors(r.data.sponsors);
      setTiers(r.data.tiers);
      setSummary(r.data.summary);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function aiTiers() {
    setBusy(true);
    setErr('');
    setMsg(t('sp_ai_working'));
    const r = await apiPost<{ tiers: TierDraft[]; thirdParty: boolean }>('/api/admin/sponsors/ai-tiers', {});
    setBusy(false);
    setMsg('');
    if (!r.ok || !r.data) {
      setErr(errOf(r.data) || t('sp_failed'));
      return;
    }
    setDrafts(r.data.tiers);
    setMsg(r.data.thirdParty ? t('sp_ai_third_party') : t('sp_ai_local'));
  }

  async function saveDrafts() {
    setBusy(true);
    setErr('');
    let n = 0;
    for (const d of drafts) {
      const r = await apiPost('/api/admin/sponsors/tiers', { ...d, benefits: d.benefits });
      if (r.ok) n += 1;
    }
    setBusy(false);
    setDrafts([]);
    setMsg(t('sp_ai_saved', { n }));
    await load();
  }

  async function saveTier() {
    setErr('');
    if (tier.name_zh_hk.trim().length < 2 || tier.name_zh_cn.trim().length < 2 || tier.name_en.trim().length < 2) {
      setErr(t('sp_need_tier'));
      return;
    }
    setBusy(true);
    const r = await apiPost('/api/admin/sponsors/tiers', {
      code: tier.code.trim() || null,
      name_zh_hk: tier.name_zh_hk.trim(),
      name_zh_cn: tier.name_zh_cn.trim(),
      name_en: tier.name_en.trim(),
      amount: Number(tier.amount) || 0,
      benefits: tier.benefits.split('\n').map((s) => s.trim()).filter(Boolean),
    });
    setBusy(false);
    if (!r.ok) {
      setErr(errOf(r.data) || t('sp_failed'));
      return;
    }
    setTier({ code: '', name_zh_hk: '', name_zh_cn: '', name_en: '', amount: '', benefits: '' });
    setMsg(t('sp_saved'));
    await load();
  }

  async function saveSponsor() {
    setErr('');
    if (sp.company.trim().length < 2) {
      setErr(t('sp_need_company'));
      return;
    }
    setBusy(true);
    const r = await apiPost('/api/admin/sponsors', {
      company: sp.company.trim(),
      contact_name: sp.contact_name.trim(),
      contact_email: sp.contact_email.trim(),
      contact_phone: sp.contact_phone.trim(),
      tier_id: sp.tier_id || null,
      pledge_amount: Number(sp.pledge_amount) || 0,
    });
    setBusy(false);
    if (!r.ok) {
      setErr(errOf(r.data) || t('sp_failed'));
      return;
    }
    setSp({ company: '', contact_name: '', contact_email: '', contact_phone: '', tier_id: '', pledge_amount: '' });
    setMsg(t('sp_saved'));
    await load();
  }

  return (
    <div className="max-w-4xl">
      <h2 className="font-bold text-lg mb-1">{t('admin_sponsors')}</h2>
      <p className="text-xs mb-4" style={{ color: 'var(--color-text-secondary)' }}>
        {t('sp_intro')}
      </p>
      {msg && <p className="text-sm mb-2" style={{ color: 'var(--color-primary)' }}>{msg}</p>}
      {err && <p className="text-sm mb-2" style={{ color: 'var(--color-danger)' }}>{err}</p>}

      {/* ── pipeline 漏斗 ─────────────────────────────────────── */}
      {summary && (
        <section className="rounded-xl p-3 mb-5" style={card}>
          <h3 className="font-bold mb-2">{t('sp_pipeline')}</h3>
          <div className="flex flex-wrap gap-3 text-sm mb-2">
            {STAGES.map((s) => (
              <span key={s} style={{ color: 'var(--color-text-secondary)' }}>
                {t(`sp_stage_${s}`)} <b style={{ color: 'var(--color-text)' }}>{summary.byStage[s] ?? 0}</b>
              </span>
            ))}
          </div>
          <p className="text-sm">
            {t('sp_total_pledged')}：<b>{money(summary.pledged)}</b> ｜ {t('sp_total_paid')}：<b>{money(summary.paid)}</b> ｜{' '}
            {t('sp_outstanding')}：<b style={{ color: summary.outstanding > 0 ? 'var(--color-danger)' : undefined }}>{money(summary.outstanding)}</b>
          </p>
        </section>
      )}

      {/* ── 方案 ＋ AI 草擬 ───────────────────────────────────── */}
      <section className="rounded-xl p-3 mb-5" style={card}>
        <h3 className="font-bold mb-2">{t('sp_tiers')}</h3>
        <ul className="text-sm mb-2">
          {tiers.length === 0 ? (
            <li style={{ color: 'var(--color-text-secondary)' }}>{t('empty_none')}</li>
          ) : (
            tiers.map((x) => (
              <li key={x.id} className="py-1">
                {x.name_zh_hk}／{x.name_en} ｜ {money(x.amount)} ｜ {t('sp_benefit_n', { n: JSON.parse(x.benefits).length })}
                {x.status === 'inactive' ? ` ｜ ${t('status_disabled')}` : ''}
              </li>
            ))
          )}
        </ul>

        <button type="button" disabled={busy} onClick={() => void aiTiers()} className="rounded-lg px-3 py-1.5 text-sm font-semibold mb-3" style={btn}>
          {t('sp_ai_tiers')}
        </button>

        {drafts.length > 0 && (
          <div className="rounded-lg p-2 mb-3" style={{ border: '1px solid var(--color-primary)' }}>
            <p className="text-xs mb-2" style={{ color: 'var(--color-primary)' }}>
              {t('sp_ai_review')}
            </p>
            <ul className="text-sm mb-2">
              {drafts.map((d, i) => (
                <li key={i} className="py-1">
                  <b>{d.name_zh_hk}</b> ｜ {money(d.amount)} ｜ {d.benefits.length} {t('sp_benefit_unit')}
                </li>
              ))}
            </ul>
            <button type="button" disabled={busy} onClick={() => void saveDrafts()} className="rounded-lg px-3 py-1.5 text-sm font-semibold" style={btn}>
              {t('sp_ai_save_all')}
            </button>
          </div>
        )}

        <input style={field} placeholder={t('sp_tier_code')} value={tier.code} onChange={(e) => setTier({ ...tier, code: e.target.value })} />
        <div className="grid grid-cols-3 gap-2 mt-2">
          <input style={field} placeholder={t('sp_name_hk')} value={tier.name_zh_hk} onChange={(e) => setTier({ ...tier, name_zh_hk: e.target.value })} />
          <input style={field} placeholder={t('sp_name_cn')} value={tier.name_zh_cn} onChange={(e) => setTier({ ...tier, name_zh_cn: e.target.value })} />
          <input style={field} placeholder={t('sp_name_en')} value={tier.name_en} onChange={(e) => setTier({ ...tier, name_en: e.target.value })} />
        </div>
        <input
          style={{ ...field, marginTop: 8 }}
          placeholder={t('sp_tier_amount')}
          inputMode="numeric"
          value={tier.amount}
          onChange={(e) => setTier({ ...tier, amount: e.target.value.replace(/[^0-9]/g, '') })}
        />
        <textarea
          style={{ ...field, marginTop: 8 }}
          rows={4}
          placeholder={t('sp_tier_benefits')}
          value={tier.benefits}
          onChange={(e) => setTier({ ...tier, benefits: e.target.value })}
        />
        <button type="button" disabled={busy} onClick={() => void saveTier()} className="rounded-lg px-4 py-1.5 text-sm font-semibold mt-2" style={btn}>
          {t('sp_tier_new')}
        </button>
      </section>

      {/* ── 贊助商 ───────────────────────────────────────────── */}
      <section className="rounded-xl p-3 mb-5" style={card}>
        <h3 className="font-bold mb-2">{t('sp_sponsors')}</h3>
        <ul className="text-sm mb-3">
          {sponsors.length === 0 ? (
            <li style={{ color: 'var(--color-text-secondary)' }}>{t('empty_none')}</li>
          ) : (
            sponsors.map((s) => (
              <li key={s.id} className="py-1">
                <Link to={`/admin/sponsors/${s.id}`} style={{ color: 'var(--color-primary)' }}>
                  {s.company}
                </Link>{' '}
                ｜ {s.tier_name_zh_hk ?? '—'} ｜ <b>{t(`sp_stage_${s.stage}`)}</b> ｜ {money(s.pledge_amount)}
                {s.paid_amount > 0 ? ` ／ ${t('sp_paid')} ${money(s.paid_amount)}` : ''}
                {s.benefits_total > 0 ? ` ｜ ${t('sp_benefit_n', { n: s.benefits_total })}（${s.benefits_done} ✓）` : ''}
              </li>
            ))
          )}
        </ul>
        <input style={field} placeholder={t('sp_company')} value={sp.company} onChange={(e) => setSp({ ...sp, company: e.target.value })} />
        <div className="grid grid-cols-3 gap-2 mt-2">
          <input style={field} placeholder={t('sp_contact_name')} value={sp.contact_name} onChange={(e) => setSp({ ...sp, contact_name: e.target.value })} />
          <input style={field} placeholder={t('sp_contact_email')} value={sp.contact_email} onChange={(e) => setSp({ ...sp, contact_email: e.target.value })} />
          <input style={field} placeholder={t('sp_contact_phone')} value={sp.contact_phone} onChange={(e) => setSp({ ...sp, contact_phone: e.target.value })} />
        </div>
        <div className="grid grid-cols-2 gap-2 mt-2">
          <select style={field} value={sp.tier_id} onChange={(e) => setSp({ ...sp, tier_id: e.target.value })}>
            <option value="">{t('sp_tier')}…</option>
            {tiers.map((x) => (
              <option key={x.id} value={x.id}>
                {x.name_zh_hk}（{money(x.amount)}）
              </option>
            ))}
          </select>
          <input
            style={field}
            placeholder={t('sp_pledge')}
            inputMode="numeric"
            value={sp.pledge_amount}
            onChange={(e) => setSp({ ...sp, pledge_amount: e.target.value.replace(/[^0-9]/g, '') })}
          />
        </div>
        <button type="button" disabled={busy} onClick={() => void saveSponsor()} className="rounded-lg px-4 py-1.5 text-sm font-semibold mt-2" style={btn}>
          {t('sp_sponsor_new')}
        </button>
      </section>
    </div>
  );
}
